import { Button, Group, Select, Text } from '@mantine/core';
import { type ReactNode, useMemo } from 'react';
import { useGetStoreById, useGetStores } from '../../hooks/stores/queries';
import { getReferenceId } from '@washday/utilities';

const ALL_STORES_VALUE = 'all';

type StoreReference =
  | string
  | { _id?: string; id?: string }
  | null
  | undefined;

interface CatalogAvailabilitySelectProps {
  activeStoreId: string;
  activeStoreFallbackLabel: string;
  allStoresDisabled?: boolean;
  allStoresLabel: string;
  disabled?: boolean;
  errorMessage: string;
  isStoreOptionDisabled?: (storeId: string) => boolean;
  label: ReactNode;
  onChange: (storeId: string | null) => void;
  retryLabel: string;
  value: StoreReference;
}

const CatalogAvailabilitySelect = ({
  activeStoreId,
  activeStoreFallbackLabel,
  allStoresDisabled = false,
  allStoresLabel,
  disabled = false,
  errorMessage,
  isStoreOptionDisabled = () => false,
  label,
  onChange,
  retryLabel,
  value
}: CatalogAvailabilitySelectProps) => {
  const storesQuery = useGetStores(true);
  const activeStoreQuery = useGetStoreById(Boolean(activeStoreId), activeStoreId);
  const selectableStores = useMemo(() => {
    const stores = new Map<
      string,
      { _id: string; name: string; isActive?: boolean }
    >();
    (storesQuery.data || []).forEach((store) => {
      if (store.isActive !== false && store._id) {
        stores.set(store._id, {
          _id: store._id,
          name: store.name || store._id,
          isActive: store.isActive
        });
      }
    });
    if (activeStoreQuery.data?._id) {
      if (activeStoreQuery.data.isActive === false) {
        stores.delete(activeStoreQuery.data._id);
      } else {
        stores.set(activeStoreQuery.data._id, {
          _id: activeStoreQuery.data._id,
          name: activeStoreQuery.data.name || activeStoreQuery.data._id,
          isActive: activeStoreQuery.data.isActive
        });
      }
    } else if (activeStoreId && !stores.has(activeStoreId)) {
      stores.set(activeStoreId, {
        _id: activeStoreId,
        name: activeStoreFallbackLabel,
        isActive: true
      });
    }

    return [...stores.values()].sort((left, right) => {
      if (left._id === activeStoreId) return -1;
      if (right._id === activeStoreId) return 1;
      return (left.name || '').localeCompare(right.name || '');
    });
  }, [
    activeStoreFallbackLabel,
    activeStoreId,
    activeStoreQuery.data,
    storesQuery.data
  ]);
  const selectedStoreId =
    value === null
      ? ALL_STORES_VALUE
      : getReferenceId(value) || activeStoreId;

  return (
    <Select
      value={selectedStoreId}
      onChange={(nextStoreId) => {
        if (!nextStoreId) return;
        onChange(nextStoreId === ALL_STORES_VALUE ? null : nextStoreId);
      }}
      label={label}
      allowDeselect={false}
      disabled={disabled || storesQuery.isError}
      error={storesQuery.isError ? (
        <Group gap="xs" justify="space-between" wrap="nowrap">
          <Text c="red" size="xs">{errorMessage}</Text>
          <Button
            onClick={() => void storesQuery.refetch()}
            size="compact-xs"
            variant="subtle">
            {retryLabel}
          </Button>
        </Group>
      ) : undefined}
      data={[
        {
          value: ALL_STORES_VALUE,
          label: allStoresLabel,
          disabled: allStoresDisabled
        },
        ...selectableStores.map((store) => ({
          value: store._id || '',
          label: store.name || store._id || '',
          disabled: Boolean(store._id && isStoreOptionDisabled(store._id))
        })).filter((option) => Boolean(option.value && option.label))
      ]}
    />
  );
};

export default CatalogAvailabilitySelect;
